import styled, { keyframes, css } from 'styled-components'
import { tabletBreakpoint, desktopBreakpoint, wideScreenBreakpoint } from 'react-components'

/**
 * Shimmer animation for skeleton loading states
 */
export const shimmer = keyframes`
    0% {
        opacity: 0.6;
    }
    50% {
        opacity: 1;
    }
    100% {
        opacity: 0.6;
    }
`

/**
 * Base skeleton box with shimmer animation
 */
export const SkeletonBox = styled.div<{
    $width?: string
    $height?: string
    borderRadius?: string
    margin?: string
}>`
    background-color: #e0e0e0;
    width: ${({ $width }) => $width || '100%'};
    height: ${({ $height }) => $height || '1.5rem'};
    border-radius: ${({ borderRadius }) => borderRadius || '0.5rem'};
    margin: ${({ margin }) => margin || '0'};
    flex-shrink: 0;
`

// =====================================================
// GridCardSkeleton Styles
// =====================================================

export const GridCardSkeletonContainer = styled.div`
    display: flex;
    flex-direction: column;
    background-color: ${({ theme }) => theme.colors.white};
    box-shadow:
        0px 1px 5px 0px #00000014,
        0px 2px 1px 0px #0000000a,
        0px 1px 2px 0px #0000000f;
    border-radius: 1rem;
`

export const GridCardImageWrapperSkeleton = styled.div`
    position: relative;
    width: 100%;
    height: 0;
    padding-bottom: 66.66%; /* 3:2 Aspect Ratio */
    border-radius: 1rem 1rem 0 0;
    overflow: hidden;
    background-color: #e0e0e0;
`

export const GridCardImageSkeleton = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border-radius: 1rem 1rem 0 0;
`

export const GridCardRatingBadgeSkeleton = styled.div`
    display: flex;
    align-items: center;
    position: absolute;
    bottom: 0;
    left: 0.8rem;
    min-width: 5.5rem;
    background-color: white;
    padding: 0.25rem 0.5rem 0;
    font: 400 0.875rem / 1.375rem ${({ theme }) => theme.typography.font.family.lato};
    border-radius: 0.6rem 0.6rem 0 0;
    justify-content: center;
    height: 1.5rem;

    /* Inverse radius on left side (slope up) */
    &::before {
        content: '';
        position: absolute;
        bottom: 0;
        left: -0.75rem;
        width: 0.8rem;
        height: 0.75rem;
        background: radial-gradient(circle at 0 0, transparent 0.75rem, white 0.75rem);
    }

    /* Inverse radius on right side (slope up) */
    &::after {
        content: '';
        position: absolute;
        bottom: 0;
        right: -0.75rem;
        width: 0.8rem;
        height: 0.75rem;
        background: radial-gradient(circle at 100% 0, transparent 0.75rem, white 0.75rem);
    }
`

export const GridCardContent = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    padding: 1rem;
    gap: 0.5rem;
`

export const GridCardBottomSection = styled.div`
    display: flex;
    flex-direction: column;
    margin-top: 1.25rem;
    gap: 0.25rem;
`

// =====================================================
// ObjectOverviewCardSkeleton Styles
// =====================================================

export const ObjectOverviewSkeletonContainer = styled.div(
    ({ theme }) => css`
        display: flex;
        flex-direction: column;
        background-color: transparent;
        gap: ${theme.spacing.xSmall};
        padding: 0 0 1.5rem 0;
        margin-bottom: 1.5rem;
        border-bottom: 1px solid ${theme.colors.tfwBlackOpacity10};
    `,
)

export const ObjectOverviewCardLayout = styled.div`
    display: grid;
    grid-template-columns: 1fr;
    row-gap: 1rem;
    column-gap: 1.25rem;

    @media only screen and (min-width: ${tabletBreakpoint.min}px) {
        grid-template-columns: 20rem 1fr;
        align-items: stretch;
    }

    @media only screen and (min-width: ${desktopBreakpoint.min}px) {
        grid-template-columns: 24rem 1fr;
    }
`

export const ObjectOverviewImageSection = styled.div`
    border-radius: 1rem;
    overflow: hidden;
    height: 100%;
`

export const ObjectOverviewImageSkeleton = styled(SkeletonBox)`
    width: 100%;
    height: 100%;
    aspect-ratio: 3 / 2;
    border-radius: 1rem;
`

export const ObjectOverviewContentSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    padding: 0 1rem;
    overflow: hidden;
`

export const ObjectOverviewTitleSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
`

export const ObjectOverviewFeatureRow = styled.div`
    display: flex;
    gap: 0.75rem;
    margin-top: 0.5rem;
`

export const ObjectOverviewBottomRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    width: 100%;
    margin-top: auto;

    @media only screen and (min-width: ${tabletBreakpoint.min}px) and (max-width: ${tabletBreakpoint.max}px) {
        margin-top: 1rem;
    }
`

export const ObjectOverviewPriceSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
`

// =====================================================
// CompactCardSkeleton Styles
// =====================================================

export const CompactCardSkeletonContainer = styled.div(
    ({ theme }) => css`
        display: grid;
        grid-template-columns: 1fr auto;
        gap: ${theme.spacing.small};
        align-items: stretch;
        background-color: ${theme.colors.white};
        border-radius: ${theme.typography.border.radius.xLarge};
        padding: ${theme.spacing.medium};
        box-shadow:
            0px 1px 5px 0px #00000014,
            0px 2px 1px 0px #0000000a,
            0px 1px 2px 0px #0000000f;
    `,
)

export const CompactCardContentSection = styled.div`
    display: flex;
    flex-direction: column;
    min-width: 0;
`

export const CompactCardTopContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    min-width: 0;
`

export const CompactCardBottomSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    margin-top: 2.5rem;
`

export const CompactCardImageSkeleton = styled(SkeletonBox)`
    width: 7.125rem;
    height: 7.125rem;
    border-radius: 0.75rem;
    flex-shrink: 0;

    @media only screen and (min-width: ${wideScreenBreakpoint.min}px) {
        width: 8.25rem;
        height: 8.25rem;
    }
`
